import { type FC, useEffect, useState, useCallback, useRef } from "react";
import { Button } from "components/buttons/button/Button";
import ModalWithSteps from "../../../modal/ModalWithSteps";
import { ConfirmLicensingStep } from "../../commonSteps";
import { type GroupsAssignmentModalTwoStepsProps } from "./types";
import { Context } from "enums/licenseContext";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { connect, type ConnectedProps } from "react-redux";
import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { updateLicensePackInfo } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/licenseConfirmationInfoSlice";
import PaymentModal from "features/Licensing/Packs/PaymentModal/PaymentModal";
import useToggle from "hooks/useToggle";
import { once } from "lodash";
import { type LicenseConfirmationFormRef } from "features/Licensing/ContentAssignmentModalLicensingSteps/LicenseConfirmationModalContent/LicenseConfirmationBlockV2/LicenseConfirmationBlock";

import "./groupsAssignmentModalTwoSteps.scss";

export type GroupsAssignmentModalTwoStepsAllProps = GroupsAssignmentModalTwoStepsProps & PropsFromRedux;

const GroupsAssignmentModalTwoSteps: FC<GroupsAssignmentModalTwoStepsAllProps> = (
  props: GroupsAssignmentModalTwoStepsAllProps,
) => {
  const [isDataValid, setIsDataValid] = useState(false);
  const [showPaymentModal, togglePaymentModal] = useToggle(false);
  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);
  const licenseConfirmationFormRef = useRef<LicenseConfirmationFormRef>({ getValues: () => [] });

  const { showModal, onCancel, onConfirm } = props;

  useEffect(() => {
    setIsDataValid(false);
  }, [showModal]);

  const renderModalActionsLicensingStep = useCallback(
    () => (closeModal: Function) => {
      const actionText = additionalPurchaseEnabled && props.isPurchaseNeeded ? "Proceed to Payment" : "Confirm";

      return (
        <>
          <Button
            basic
            color="blue"
            className="cancel"
            content="Cancel"
            onClick={() => {
              onCancel();
              closeModal();
            }}
          />

          <Button
            primary
            className="confirm"
            content={actionText}
            onClick={once(async () => {
              if (additionalPurchaseEnabled && props.isPurchaseNeeded) {
                props.updateLicensePackInfoAction?.(licenseConfirmationFormRef.current.getValues());
                togglePaymentModal();
                closeModal();
              } else {
                onConfirm();
                closeModal();
              }
            })}
            disabled={!isDataValid}
          />
        </>
      );
    },
    [isDataValid, onCancel, onConfirm, additionalPurchaseEnabled, props, togglePaymentModal],
  );

  const onPaymentSuccess = () => {
    togglePaymentModal();
    onConfirm();
  };

  const onClosePaymentModal = () => {
    togglePaymentModal();
    onCancel();
  };

  return (
    <>
      <ModalWithSteps
        className={"groups-assignment-modal-two-steps"}
        scrolling
        showModal={showModal}
        onCancel={onCancel}
      >
        <ConfirmLicensingStep
          header="License Confirmation"
          renderModalActions={renderModalActionsLicensingStep}
          info={{
            userIds: props.selectedUserIds,
            groupIds: props.selectedGroupIds,
          }}
          context={Context.AddPeopleToGroups}
          onIsDataValidChange={setIsDataValid}
          licenseConfirmationFormRef={licenseConfirmationFormRef}
        />
      </ModalWithSteps>
      {showPaymentModal && props.paymentPacks && (
        <PaymentModal
          paymentPacks={props.paymentPacks}
          onClose={onClosePaymentModal}
          onPaymentSuccess={onPaymentSuccess}
        />
      )}
    </>
  );
};

export { GroupsAssignmentModalTwoSteps as GroupsAssignmentModalTwoStepsUnconnected };

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  isPurchaseNeeded: state.licensing?.availableLicenses?.isPurchaseNeeded,
  paymentPacks: state.licensing.licenseConfirmationInfo.paymentPacks,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateLicensePackInfoAction: bindAction(updateLicensePackInfo, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(GroupsAssignmentModalTwoSteps);
export default ConnectedComponent;
